// Libraries
import * as React from "react";

// Utils
import {
  Action,
  FlexTrackingInfo,
  sendDelayedTrackEvent,
  sendImmediateTrackEvent,
} from "components/utility/analytics/FlexAnalyticsUtils";

// Types
import { ModuleMapProps } from "../typings";
import { HotelSearchLinkBuilder } from "components/utility/HotelSearchLinkBuilder/HotelSearchLinkBuilder";
import { isVariantEnabled, Experiment } from "components/utility/experiment";

/**
 * Custom hook to control the fullscreen map dialog behavior
 * @param props
 */
export const useFullscreenOpen = (props: ModuleMapProps) => {
  const isPwaDialogMapQuery = (value: string): boolean => {
    return props.context?.queryParams?.pwaDialogMap === value;
  };

  const [showFullscreenMap, setShowFullscreenMap] = React.useState(
    isPwaDialogMapQuery(`open-${props.flexComponent.id}`)
  );
  // Since the state cannot be used from closure, we need to use a Ref
  const showFullscreenMapRef = React.useRef(showFullscreenMap);
  showFullscreenMapRef.current = showFullscreenMap;

  const toggleShowFullscreenMap = React.useCallback(() => {
    const routeStore = props.routeStore;

    const flexTrackingInfo: FlexTrackingInfo = {
      moduleName: props.flexComponent.name,
      rfrr: !showFullscreenMapRef.current ? undefined : "close",
      action: Action.CLICK,
    };

    sendDelayedTrackEvent(flexTrackingInfo, props.analytics);

    if (routeStore) {
      routeStore.updateSearch({
        pwaDialogMap: showFullscreenMapRef.current
          ? `close-${props.flexComponent.id}`
          : `open-${props.flexComponent.id}`,
      });
    }

    setShowFullscreenMap(!showFullscreenMapRef.current);
  }, [showFullscreenMapRef.current]);

  const sendUserToSRP = () => {
    const flexTrackingInfo: FlexTrackingInfo = {
      moduleName: props.flexComponent.name,
      rfrr: "toSRP",
      action: Action.CLICK,
    };

    sendImmediateTrackEvent(flexTrackingInfo, props.analytics);
    const regionId = props.context?.searchContext?.location.id;
    const langId = props.context?.langId;

    if (regionId && langId) {
      const linkBuilder = new HotelSearchLinkBuilder()
        .withRegionId(regionId)
        .withLangId(langId.toString(10))
        .withStayLength()
        .withDaysInFuture()
        .withMapOpen();

      if (isVariantEnabled(props.context, Experiment.Blossom_SRP_Lite_V2)) {
        linkBuilder.withExpandForm();
      }

      window.location.assign(linkBuilder.build());
    }
  };

  // Handles back button
  React.useEffect(() => {
    const listenToBackButton = () => {
      if (showFullscreenMapRef.current) {
        toggleShowFullscreenMap();
      }
    };

    window.addEventListener("popstate", listenToBackButton);

    return () => window.removeEventListener("popstate", listenToBackButton);
  }, []);

  return {
    isPwaDialogMapQuery,
    toggleShowFullscreenMap,
    sendUserToSRP,
  };
};

export default useFullscreenOpen;
